// src/frontend/src/components/Simulation/FeedbackGenerationLoading.tsx
import React from 'react';

interface FeedbackGenerationLoadingProps {
  elapsedTime: number; // in seconds
}

const FeedbackGenerationLoading: React.FC<FeedbackGenerationLoadingProps> = ({ elapsedTime }) => {
  // Determine which step to show based on elapsed time
  const getStep = () => {
    if (elapsedTime < 3) return 1;
    if (elapsedTime < 6) return 2;
    if (elapsedTime < 9) return 3;
    if (elapsedTime < 12) return 4;
    return 5;
  };

  const step = getStep();
  
  // Progress percentage (capped at 90% to avoid appearing complete before it actually is)
  const progressPercentage = Math.min(90, (elapsedTime / 15) * 100);

  return (
    <div className="bg-white rounded-lg shadow-md p-6 max-w-3xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-center">Analyzing Your Conversation</h2>
      
      {/* Progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-2.5 mb-6 dark:bg-gray-700">
        <div 
          className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-in-out"
          style={{ width: `${progressPercentage}%` }}
        ></div>
      </div>
      
      {/* Steps */}
      <div className="space-y-6">
        <div className={`flex items-start ${step >= 1 ? 'text-gray-800' : 'text-gray-400'}`}>
          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3 ${step >= 1 ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-400'}`}>
            1
          </div>
          <div>
            <h3 className="font-medium">Reviewing conversation context</h3>
            <p className="text-sm">Analyzing the scenario and stakeholder profile...</p>
          </div>
          {step === 1 && (
            <div className="ml-auto">
              <div className="animate-pulse flex space-x-1">
                <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
                <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
                <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
              </div>
            </div>
          )}
        </div>
        
        <div className={`flex items-start ${step >= 2 ? 'text-gray-800' : 'text-gray-400'}`}>
          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3 ${step >= 2 ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-400'}`}>
            2
          </div>
          <div>
            <h3 className="font-medium">Evaluating communication approach</h3>
            <p className="text-sm">Assessing how effectively you engaged with the stakeholder...</p>
          </div>
          {step === 2 && (
            <div className="ml-auto">
              <div className="animate-pulse flex space-x-1">
                <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
                <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
                <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
              </div>
            </div>
          )}
        </div>
        
        <div className={`flex items-start ${step >= 3 ? 'text-gray-800' : 'text-gray-400'}`}>
          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3 ${step >= 3 ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-400'}`}>
            3
          </div>
          <div>
            <h3 className="font-medium">Identifying strengths and improvement areas</h3>
            <p className="text-sm">Determining key aspects of your stakeholder management approach...</p>
          </div>
          {step === 3 && (
            <div className="ml-auto">
              <div className="animate-pulse flex space-x-1">
                <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
                <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
                <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
              </div>
            </div>
          )}
        </div>
        
        <div className={`flex items-start ${step >= 4 ? 'text-gray-800' : 'text-gray-400'}`}>
          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3 ${step >= 4 ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-400'}`}>
            4
          </div>
          <div>
            <h3 className="font-medium">Generating specific feedback</h3>
            <p className="text-sm">Creating personalized insights on your performance...</p>
          </div>
          {step === 4 && (
            <div className="ml-auto">
              <div className="animate-pulse flex space-x-1">
                <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
                <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
                <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
              </div>
            </div>
          )}
        </div>
        
        <div className={`flex items-start ${step >= 5 ? 'text-gray-800' : 'text-gray-400'}`}>
          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3 ${step >= 5 ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-400'}`}>
            5
          </div>
          <div>
            <h3 className="font-medium">Finalizing your performance report</h3>
            <p className="text-sm">Compiling all insights into comprehensive feedback...</p>
          </div>
          {step === 5 && (
            <div className="ml-auto">
              <div className="animate-pulse flex space-x-1">
                <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
                <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
                <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <p className="text-center text-gray-500 mt-6 text-sm">
        This typically takes 10-15 seconds. Thank you for your patience.
      </p>
    </div>
  );
};

export default FeedbackGenerationLoading;